
import React, { useRef, useState, SyntheticEvent } from 'react';
import PropTypes from 'prop-types';
import { useTheme, Theme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@/components/@extended/Avatar';
import { Card, MenuItem } from '@mui/material';
import Transitions from '@/components/@extended/Transitions';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import { useNavigate } from 'react-router-dom';
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import { styled } from '@mui/material/styles';
import Badge from '@mui/material/Badge';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  dir?: 'ltr' | 'rtl';
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, dir, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} dir={dir} {...other}>
      {value === index && children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`,
  };
}

export default function Profile() {
  const theme = useTheme<Theme>();
  const [appMenuList, setAppMenuList] = useState([]);
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
  const handleClose = (event: globalThis.MouseEvent | globalThis.TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const iconBackColorOpen = 'grey.100';

  const menuItemContent = (children: React.ReactNode, linkPath: string, event_name: string, newTab = true) => (
    <Box
      onClick={() => {
        window.open(linkPath, newTab ? '_blank' : '_self');
      }}
    >
      {children}
    </Box>
  );
  const handleOpen = (): void => {
    setOpen(true);
  };

  const signOut = (): void => {
    localStorage.clear();
    navigate('/');
  };
  const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: '#1976d2', // Set background color to blue
      color: '#1976d2',
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        animation: 'ripple 1.2s infinite ease-in-out',
        border: '1px solid currentColor',
        content: '""',
      },
    },
    '@keyframes ripple': {
      '0%': {
        transform: 'scale(.8)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(2.4)',
        opacity: 0,
      },
    },
  }));

  return (
    <Box sx={{
      flexShrink: 0,
      ml: { xs: 0.5, sm: 0.75 },
      minWidth: 0,
    }}>
      <ButtonBase
        sx={{
          p: { xs: 0.25, sm: 0.5 },
          bgcolor: open ? iconBackColorOpen : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 },
          minWidth: 0,
          width: '100%',
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack
          direction="row"
          spacing={{ xs: 0.5, sm: 1.25 }}
          alignItems="center"
          sx={{
            p: { xs: 0.25, sm: 0.5 },
            minWidth: 0,
            width: '100%',
          }}
        >
          <StyledBadge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot">
            <Avatar
              alt="profile user"
              size="sm"
              children={undefined}
              type={undefined}
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
              }}
            />
          </StyledBadge>
          <Typography
            variant="subtitle1"
            sx={{
              textTransform: 'capitalize',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              display: { xs: 'none', sm: 'block' },
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Huewine
          </Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              width: { xs: 280, sm: 290 },
              minWidth: { xs: 240, sm: 260 },
              maxWidth: { xs: '90vw', sm: 290 },
            }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Card elevation={0}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar alt="profile user" sx={{ width: 32, height: 32 }} children={undefined} type={undefined} />
                          <Stack>
                            <Typography variant="h6">Admin</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Admin
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item>
                        {menuItemContent(
                          <>
                            <Tooltip title="Logout">
                              <IconButton size="large" sx={{ color: 'text.primary' }} onClick={signOut}>
                                <LogoutOutlined />
                              </IconButton>
                            </Tooltip>
                          </>,
                          ``,
                          'Logout',
                          false
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs">
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                        }}
                        icon={<UserOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
                        label="Profile"
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                        }}
                        icon={<SettingOutlined style={{ marginBottom: 0, marginRight: '10px' }} />}
                        label="Setting"
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab
                      handleLogout={function (): void {
                        throw new Error('Function not implemented.');
                      }}
                    />
                  </TabPanel>
                  <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel>
                </Card>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  dir: PropTypes.string,
};
